import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import {
  CONVERSION_READINESS_LABELS,
  CONVERSION_STAGE_LABELS,
  LOST_REASON_LABELS,
  TRIAL_STATUS_LABELS,
  readinessBadgeClass,
  trackDealerTrialEvent,
} from "../../../constants/automapDealerTrialConstants";
import { PLAN_KEYS, PLAN_LABELS } from "../../../constants/automapDealerPlanConstants";
import {
  addTrialConversation,
  completeTrialFollowUp,
  createTrialFollowUp,
  fetchDealerTrialDetail,
  postTrialDecision,
} from "../../../services/automapDealerTrialApi";
import { AdminDealerNav } from "./dealerAdminShared";

function AdminDealerTrialDetailContent({ embedded = false, dealerId: dealerIdProp }) {
  const { dealerId: paramDealerId } = useParams();
  const dealerId = dealerIdProp || paramDealerId;
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extendDate, setExtendDate] = useState("");
  const [extendNote, setExtendNote] = useState("");
  const [lostReason, setLostReason] = useState("not_interested");
  const [convertPlanKey, setConvertPlanKey] = useState("starter");
  const [convSummary, setConvSummary] = useState("");
  const [convType, setConvType] = useState("checkin");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchDealerTrialDetail(dealerId);
      setData(payload);
      trackDealerTrialEvent("dealer_trial_detail_viewed", { dealerId });
    } catch (err) {
      setError(err.message || "Error al cargar trial.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    load();
  }, [load]);

  const runDecision = async (action, body = {}) => {
    if (!canManage) return;
    try {
      await postTrialDecision(dealerId, action, body);
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    }
  };

  const createSuggestedFollowUp = async (suggestion) => {
    await createTrialFollowUp(dealerId, {
      title: suggestion.title,
      description: suggestion.description,
      followUpType: suggestion.followUpType,
      priority: suggestion.priority,
    });
    await load();
  };

  const submitConversation = async () => {
    if (!convSummary.trim()) return;
    await addTrialConversation(dealerId, {
      conversationType: convType,
      summary: convSummary,
      outcome: "neutral",
    });
    setConvSummary("");
    await load();
  };

  if (loading) return <p className="dealer-state-box">Cargando trial…</p>;
  if (error) return <p className="dealer-state-box">{error}</p>;
  if (!data?.trial) return null;

  const { trial, performance, followUps, conversations, suggestedFollowUps, recommendedActions } = data;

  const body = (
    <>
        <section className="dealer-state-box">
          <h3>Resumen trial</h3>
          <div className="dashboard-lead-stats">
            <span>Estado: {TRIAL_STATUS_LABELS[trial.trialStatus]}</span>
            <span>Tipo: {trial.trialType}</span>
            <span>Días restantes: {trial.daysRemaining ?? "—"}</span>
            <span>Owner: {trial.assignedOwner || "—"}</span>
            <span>Stage: {CONVERSION_STAGE_LABELS[trial.conversionStage] || trial.conversionStage}</span>
            <span>Interés plan: {trial.paidPlanInterest}</span>
            <span>Próximo follow-up: {trial.nextFollowUpAt?.slice(0, 10) || "—"}</span>
          </div>
          <p>
            Readiness:{" "}
            <span className={readinessBadgeClass(trial.conversionReadiness)}>
              {CONVERSION_READINESS_LABELS[trial.conversionReadiness]} ({trial.readinessScore ?? "—"}/100)
            </span>
          </p>
        </section>

        <section className="dealer-state-box">
          <h3>Desempeño en trial</h3>
          <div className="dashboard-lead-stats">
            <span>Vistas: {performance?.viewsDuringTrial ?? 0}</span>
            <span>WhatsApp: {performance?.whatsappClicks ?? 0}</span>
            <span>Leads: {performance?.leadsDuringTrial ?? 0}</span>
            <span>Calificados: {performance?.qualifiedLeads ?? 0}</span>
            <span>Respuesta: {performance?.responseRate ?? "—"}%</span>
            <span>Frescura: {performance?.inventoryFreshnessPercent ?? "—"}%</span>
          </div>
        </section>

        {canManage ? (
          <section className="dealer-state-box">
            <h3>Decisión de conversión</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("mark_ready_to_convert")}>Listo convertir</button>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("schedule_value_review")}>Agendar value review</button>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("mark_pricing_discussed")}>Pricing discutido</button>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("mark_proposal_sent")}>Propuesta enviada</button>
              <Link className="dealer-portal-btn" to={`/admin/dealers/${dealerId}/plan`}>Gestionar plan</Link>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("pause_trial")}>Pausar</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem", alignItems: "flex-end" }}>
              <label>
                Plan pago
                <select value={convertPlanKey} onChange={(e) => setConvertPlanKey(e.target.value)}>
                  {PLAN_KEYS.filter((k) => k !== "free").map((k) => (
                    <option key={k} value={k}>{PLAN_LABELS[k]}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="dealer-portal-btn primary"
                onClick={() => runDecision("convert_to_paid", { planKey: convertPlanKey, note: "Conversión trial-to-paid" })}
              >
                Convertir a plan pago
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}>
              <label>
                Extender hasta
                <input type="date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} />
              </label>
              <label>
                Nota
                <input value={extendNote} onChange={(e) => setExtendNote(e.target.value)} placeholder="Razón extensión" />
              </label>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("extend_trial", { trialEndDate: extendDate, note: extendNote })}>Extender</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.75rem", alignItems: "flex-end" }}>
              <label>
                Razón pérdida
                <select value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
                  {Object.entries(LOST_REASON_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              <button type="button" className="dealer-portal-btn" onClick={() => runDecision("mark_lost", { lostReason, note: "Marcado perdido" })}>Marcar perdido</button>
            </div>
          </section>
        ) : null}

        {suggestedFollowUps?.length ? (
          <section className="dealer-state-box">
            <h3>Follow-ups sugeridos</h3>
            <ul>
              {suggestedFollowUps.map((s) => (
                <li key={s.followUpType}>
                  <strong>{s.title}</strong> — {s.description}
                  {canManage ? (
                    <button type="button" className="dealer-portal-btn" style={{ marginLeft: "0.5rem" }} onClick={() => createSuggestedFollowUp(s)}>
                      Crear tarea
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="dealer-state-box">
          <h3>Follow-ups</h3>
          {!followUps?.length ? <p className="muted">Sin follow-ups.</p> : (
            <ul>
              {followUps.map((f) => (
                <li key={f.id}>
                  {f.title} · {f.status} · {f.followUpType}
                  {canManage && f.status === "open" ? (
                    <button type="button" className="dealer-portal-btn" onClick={() => completeTrialFollowUp(f.id, { outcome: "dealer_engaged" }).then(load)}>
                      Completar
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dealer-state-box">
          <h3>Log de conversaciones</h3>
          {canManage ? (
            <div style={{ marginBottom: "0.75rem" }}>
              <select value={convType} onChange={(e) => setConvType(e.target.value)}>
                <option value="checkin">Check-in</option>
                <option value="performance_review">Performance review</option>
                <option value="pricing">Pricing</option>
                <option value="proposal">Propuesta</option>
              </select>
              <textarea value={convSummary} onChange={(e) => setConvSummary(e.target.value)} placeholder="Resumen conversación" rows={3} style={{ width: "100%", marginTop: "0.35rem" }} />
              <button type="button" className="dealer-portal-btn" onClick={submitConversation}>Agregar nota</button>
            </div>
          ) : null}
          {!conversations?.length ? <p className="muted">Sin conversaciones.</p> : (
            <ul>
              {conversations.map((c) => (
                <li key={c.id}>
                  <strong>{c.conversationType}</strong> · {c.createdAt?.slice(0, 10)} — {c.summary}
                </li>
              ))}
            </ul>
          )}
        </section>

        {recommendedActions?.length ? (
          <section className="dealer-state-box">
            <h3>Acciones recomendadas</h3>
            <ul>{recommendedActions.map((a) => <li key={a.id}>{a.message}</li>)}</ul>
          </section>
        ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Trial"
        title={trial.businessName || data.dealer?.businessName}
        description={`Trial ${TRIAL_STATUS_LABELS[trial.trialStatus] || trial.trialStatus}`}
        navigation={<AdminDealerNav active="trials" />}
        primaryActions={
          <Link className="admin-btn" to={`/admin/dealers/${dealerId}/performance`}>
            Performance
          </Link>
        }
      />
      <main className="admin-console__main wrap">{body}</main>
    </AdminPageLayout>
  );
}

export { AdminDealerTrialDetailContent };

function AdminDealerTrialDetailPageWrapper(props) {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerTrialDetailContent {...props} />
    </RequirePermission>
  );
}

export default AdminDealerTrialDetailPageWrapper;
